import { css } from '@emotion/react';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { FC, useState } from 'react';

import { useAdminRequestHiringGraphic } from '../hooks/useAdminRequestHiringGraphic';
import { toast } from 'react-toastify';
import { Employer } from '../types';
import { staticFileLocation } from '@server/constants';
import { Close } from '@mui/icons-material';
import { GraphicsIcon } from '../../../vectors/index';

type AdminCustomGraphicsModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  handleRequestGraphicSuccess: () => void;
  handleUploadImage: () => void;
  handleContinueWithout: () => void;
  item: Employer
};

const AdminCustomGraphicsModal: FC<AdminCustomGraphicsModalProps> = ({
  isOpen,
  handleClose,
  handleRequestGraphicSuccess,
  handleUploadImage,
  handleContinueWithout,
  item
}) => {
  const [alertText, setAlertText] = useState<string>('');
  const { requestHiringGraphic, isRequestHiringGraphicLoading } =
    useAdminRequestHiringGraphic({ setAlertText });

  const handleRequestHiringGraphic = () => {
    const options = {
      onSuccess: (data: { message: string }) => {
        toast.success(data.message)
        handleRequestGraphicSuccess();
      },
    };
    requestHiringGraphic({
      user_id: item.id,
      ...(item.enterprise ? { is_enterprise: 1 } : {}),
    }, options)
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleClose}
        >
          <Close />
        </IconButton>
        <br />
        <GraphicsIcon />
        <Typography variant="h4" sx={{ pt: 3 }}>
          Need a Custom Hiring Graphic?
        </Typography>
      </DialogTitle>
      <DialogContent>
        {alertText && <Alert severity="error">{alertText}</Alert>}
        <Grid sx={{ marginBottom: 2 }} container spacing={4}>
          <Grid
            item
            xs={12}
            sm={6}
            display="flex"
            alignItems="center"
            justifyContent={{ xs: 'center', sm: 'flex-start' }}
          >
            <img
              src={`${staticFileLocation}img/graphic-img-edit.png`}
              alt="Hiring Graphic Example"
              css={css`
                width: 100%;
                display: block;
                max-width: 400px;
                border-radius: 8px;
              `}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack spacing={2} textAlign="center">
              <Typography variant="h3" textTransform="uppercase">
                Request a Custom Hiring Graphic Here!
              </Typography>
              <Typography variant="body1" component="p">
                We'll design it with your logo and images, and upload it to your
                account so you can post to facebook or anywhere on the web in
                seconds to attract more applications!
              </Typography>
              <Button
                variant="contained"
                color="secondary"
                disabled={isRequestHiringGraphicLoading}
                onClick={handleRequestHiringGraphic}
              >
                Request Your Custom Graphic
              </Button>
              <Button
                variant="contained"
                color="success"
                disabled={isRequestHiringGraphicLoading}
                onClick={handleUploadImage}
              >
                Upload Image
              </Button>
              <Button
                variant="contained"
                color="warning"
                disabled={isRequestHiringGraphicLoading}
                onClick={handleContinueWithout}
              >
                Continue Without Image
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default AdminCustomGraphicsModal;
